import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import {
  Home as HomeIcon,
  Book as BookIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  Label as LabelIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { Fab } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../Navigation';
import { authUtils } from '../../services/api';
import { useSafeNavigate } from '../../hooks/useUnsavedChangesWarning';

const drawerWidth = 280;

function Layout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true); // Menu ouvert par défaut sur desktop
  const safeNavigate = useSafeNavigate();
  const location = useLocation();
  const isAuthenticated = authUtils.isAuthenticated();

  // URL de la documentation selon la langue
  const tutorialUrl = i18n.language === 'en' 
    ? 'https://libranize.com/documentation-en.html'
    : 'https://libranize.com/documentation-fr.html';

  const navigationItems = [
    { text: t('menu.home'), icon: <HomeIcon />, path: '/' },
    { text: t('menu.books'), icon: <BookIcon />, path: '/livres' },
    { text: t('menu.addBook'), icon: <AddIcon />, path: '/livres/ajouter' },
    { text: t('menu.authors'), icon: <PersonIcon />, path: '/auteurs' },
    { text: t('menu.publishers'), icon: <BusinessIcon />, path: '/editeurs' },
    { text: t('menu.tags'), icon: <LabelIcon />, path: '/tags' },
    { text: t('menu.libraries'), icon: <LocationIcon />, path: '/bibliotheques' },
    { text: t('menu.tutorial'), icon: <MenuBookIcon />, path: tutorialUrl, external: true },
    { text: t('menu.about'), icon: <InfoIcon />, path: '/about' },
  ];

  // Vérifier si nous sommes sur une page d'authentification
  const isAuthPage = ['/login', '/register'].includes(location.pathname) || 
                     location.pathname.startsWith('/invitation/');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    safeNavigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            {item.external ? (
              <ListItemButton
                component="a"
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '10',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                />
              </ListItemButton>
            ) : (
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    borderRight: `3px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path 
                      ? theme.palette.primary.main 
                      : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      color: location.pathname === item.path 
                        ? theme.palette.primary.main 
                        : 'inherit'
                    }
                  }}
                />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Si c'est une page d'authentification ou si l'utilisateur n'est pas connecté, afficher seulement le contenu
  if (isAuthPage || !isAuthenticated) {
    return (
      <Box sx={{ width: '100%', minHeight: '100vh' }}>
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
      {/* Navigation moderne en haut */}
      <Navigation 
        onMenuToggle={handleDrawerToggle}
        onDesktopMenuToggle={() => setDesktopOpen(!desktopOpen)}
        desktopMenuOpen={desktopOpen}
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <Box
          component="nav"
          sx={{ 
            width: { 
              md: desktopOpen ? drawerWidth : 0 
            }, 
            flexShrink: { md: 0 },
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {/* Drawer mobile */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                top: { xs: '56px', sm: '64px' }, // Hauteur AppBar: 56px sur mobile, 64px sur desktop
                height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' }, // Ajuster la hauteur en conséquence
              },
            }}
          >
            {drawer}
          </Drawer>
          
          {/* Drawer desktop - escamotable */}
          <Drawer
            variant="persistent"
            open={desktopOpen}
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                borderRight: '1px solid #e0e0e0',
                top: '64px', // Ajuster pour la hauteur de l'AppBar
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { 
              xs: '100%',
              md: desktopOpen 
                ? `calc(100% - ${drawerWidth}px)` 
                : '100%' 
            },
            paddingLeft: {
              xs: '0px',
              sm: '24px',
            },
            paddingRight: {
              xs: '0px',
              sm: '24px',
            },
            backgroundColor: theme.palette.background.default, // blue!!!
            minHeight: 'calc(100vh - 64px)', // Ajuster pour la hauteur de l'AppBar
            marginTop: '64px', // Compenser l'AppBar fixe
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Container maxWidth="xl" sx={{ py: 0 }}>
            <Box sx={{ py: 3 }}>
              {children}
            </Box>
          </Container>
          
          {/* Bouton flottant pour ajouter un livre - masqué sur la page d'ajout */}
          {location.pathname !== '/livres/ajouter' && (
            <Fab
              color="primary"
              size="large"
              onClick={() => {
                // Faire défiler vers le haut avant de naviguer
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // Petite pause pour laisser le scroll se terminer
                setTimeout(() => {
                  safeNavigate('/livres/ajouter');
                }, 100);
              }}
              sx={{
                position: 'fixed',
                bottom: { xs: 16, sm: 24, md: 32 },
                right: { xs: 16, sm: 24, md: 32 },
                zIndex: 9999,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              <AddIcon />
            </Fab>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
